import React, { useState, useEffect, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { PayloadAction } from '@reduxjs/toolkit';
import { Spin, Row, Col } from 'antd';
import pluralize from 'pluralize';

import { getAssignmentTemplates } from 'redux/reducers/assignmentSlice';
import { assignmentTemplateCreatedAtSelector } from 'redux/selectors/assignmentSelectors';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  UserPagedAssignmentTemplatesResponseModel,
  AssignmentTemplateSummaryResponseModel,
} from 'api/backofficeApiClient';
import AssignmentCard from '../AssignmentCard';

export default function AssignmentTemplatesSection() {
  const latestRequestId = useRef(0);
  const infiniteScrollParentRef = useRef(null);
  const [templates, setTemplates] = useState<
    AssignmentTemplateSummaryResponseModel[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const assignmentTemplateCreatedAt = useAppSelector(
    assignmentTemplateCreatedAtSelector,
  );

  useEffect(() => {
    initInfinityScroll();
  }, [assignmentTemplateCreatedAt]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setTemplates([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetTemplates = useCallback(
    (result: PayloadAction<UserPagedAssignmentTemplatesResponseModel>) => {
      const loadedTemplates = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newTemplates = [...templates, ...loadedTemplates];
      const hasMoreRows = newTemplates.length < totalCount;

      setHasMore(hasMoreRows);
      setTemplates(newTemplates);
      setTotalCount(totalCount);
    },
    [templates],
  );

  const loadTemplates = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const result = await dispatch(
        getAssignmentTemplates({ requestId, pageNumber }),
      );

      if (result.meta.arg.requestId === latestRequestId.current) {
        if (getAssignmentTemplates.fulfilled.match(result)) {
          onSetTemplates(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetTemplates],
  );

  const getTotalCountMessage = () =>
    pluralize('assignment template', totalCount, true);

  const getNoAssignmentTemplatesMessage = () =>
    'You have no templates created.';

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[10, 10]}>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountMessage()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">
                      {getNoAssignmentTemplatesMessage()}
                    </span>
                  )}
                </>
              )}

              {isVisible && (
                <InfiniteScroll
                  pageStart={-1}
                  loadMore={loadTemplates}
                  hasMore={hasMore}
                  loader={<Spin key={0} />}
                  useWindow={false}
                  getScrollParent={() => infiniteScrollParentRef.current}
                >
                  <Row gutter={[20, 20]}>
                    {templates.map(
                      (template: AssignmentTemplateSummaryResponseModel) => (
                        <Col
                          key={template.id}
                          xs={24}
                          md={12}
                          className="d-flex"
                        >
                          <AssignmentCard
                            assignment={template}
                            isTemplate={true}
                          />
                        </Col>
                      ),
                    )}
                  </Row>
                </InfiniteScroll>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}
